import React, {useEffect, useState} from "react";
import NavBar from "../components/navBar/navBar";
import NaviBarData from "../components/navBar/dataForNavBar"
import {Route, Switch, useHistory, useParams} from "react-router-dom";
import Campaign from "./campaign/campaign";
import {RoutePath} from "../router/routePath"
import {IProvider, ProviderContext} from "./providerContext";
import Spinner from "../components/spinner/spinner";
import useProviderAuth from "./login/useProviderAuth";
import NewGroup from "./patients/newGroup";
import NewCampaign from "./campaign/newCampaign";
import Patients from "./patients/patients";
import NotFound from "./routerError/notFound";
import Notification, {NotificationType} from "../components/modal/notification";
import {MPrompt, PromptContext} from "./promptContext";
import {SharedContextProvider} from "./sharedContextProvider";
import {GrayLog} from "../grayLog/grayLog";
import {LogEventName} from "../grayLog/logKey";
import {GraylogContext, LogEmployee} from "./graylogContext";
import Dashboard from "./dashboard/dashboard";
import {isDebug} from "../appConfig";
import Alert, {AlertAction} from "../components/modal/alert";
import {differ} from "../util/util";

interface ShowToast {
    isVisible: boolean,
    title: string,
    type: NotificationType,
    onClose?: () => void
}

interface ShowAlert {
    isVisible: boolean,
    title: string,
    message?: string,
    onResolve?: {title: string, onClick?: () => void},
    onReject?: {title: string, onClick?: () => void}
}

interface Param {
    token: string
}
function Admin() {
    const [isShowSpinner, setIsShowSpinner] = useState<boolean>(false)
    const history = useHistory()
    const {token} = useParams<Param>()
    const {login, provider} = useProviderAuth()
    const [showToast, setShowToast] = useState<ShowToast>({isVisible: false, title: "", type: NotificationType.info})
    const [showAlert, setShowAlert] = useState<ShowAlert>({isVisible: false, title: ""})

    const promptContextValue: MPrompt = {
        showInfo: (info, complete) => {
            info && setShowToast({
                isVisible: true,
                title: info,
                type: NotificationType.info,
                onClose: complete,
            })
        },
        showSuccess: (title, complete) => {
            title && setShowToast({
                isVisible: true,
                title: title,
                type: NotificationType.success,
                onClose: complete,
            })
        },
        showError: (error, complete) => {
            let errMsg = ""
            if (typeof error === "string") {
                errMsg = error
            } else if (error.msg) {
                errMsg = isDebug ? `${error.code} - ${error.msg}` : error.msg
            }
            setShowToast({
                isVisible: true,
                title: errMsg || "Something went wrong. Please try again.",
                type: NotificationType.error,
                onClose: complete,
            })
        },
        showAlert: (title, message, onResolve, onReject) => {
            title && setShowAlert({
                isVisible: true,
                title: title,
                message: message,
                onResolve: onResolve,
                onReject: onReject,
            })
        },
    }

    const getLogEmployee = (provider: IProvider): LogEmployee => {
        return {
            clinicId: provider.clinicID,
            mEmployeeId: provider.mEmployeeID,
            employeeName: `${provider.employeeLastName}, ${provider.employeeFirstName}`,
        }
    }

    useEffect(() => {
        setIsShowSpinner(true)
        login(token, (provider) => {
            history.replace(RoutePath.dashboard)
            setIsShowSpinner(false)
            GrayLog.updateCommon(`${provider.clinicID}`)
            GrayLog.event(LogEventName.openSite, {isSuccessful: true, ...getLogEmployee(provider)})
        }, ({code, msg}) => {
            const errMsg = `${code} - ${msg}`
            redirectToLoginFailed(errMsg)
            setIsShowSpinner(false)
            GrayLog.event(LogEventName.openSite, {isSuccessful: false, errMsg: errMsg})
        })
    }, [])

    const redirectToLoginFailed = (errMsg: string) => {
        history.replace({
            pathname: RoutePath.loginFailed,
            state: {
                errMsg: errMsg,
            },
        })
    }

    /*
    *
    * <Route path={RoutePath.conversations} exact component={Conversation} />
                    <Route path={RoutePath.surveys} exact component={Surveys} />
                    <Route path={RoutePath.surveysTemplate} exact component={SurveyTemplate} />
                    <Route path={RoutePath.surveysBuilder} exact component={SurveyBuilder} />
                    <Route path={RoutePath.engagementAnalytics} exact component={ComingSoonView} />
                    <Route path={RoutePath.costAnalytics} exact component={ComingSoonView} />
    * */
    const $routerContent = (
        <div className={"w-full flex flex-col items-center justify-center"}>
            <div className={"container mb-12"}>
                <Switch>
                    <Route path={RoutePath.dashboard} exact component={Dashboard} />
                    <Route path={RoutePath.campaign} exact component={Campaign} />
                    <Route path={RoutePath.newCampaign} exact component={NewCampaign} />
                    <Route path={RoutePath.patients} exact component={Patients} />
                    <Route path={RoutePath.newGroup} exact component={NewGroup} />
                    <Route path={"*"} >
                        <NotFound isLoading={isShowSpinner} />
                    </Route>
                </Switch>
            </div>
        </div>
    )

    const $toastNotification = (
        <Notification
            isOpen={showToast.isVisible}
            type={showToast.type}
            title={showToast.title}
            onClose={() => {
                setShowToast({
                    ...showToast,
                    isVisible: false,
                })
                showToast.onClose && showToast.onClose()
            }}
        />
    )

    const onCloseAlert = () => {
        setShowAlert({
            ...showAlert,
            isVisible: false,
        })
    }

    let alertRejectAction: AlertAction | undefined
    if (showAlert.onReject) {
        alertRejectAction = {title: showAlert.onReject.title, onClick: () => {
            onCloseAlert()
            showAlert.onReject?.onClick && differ(showAlert.onReject.onClick)
        }}
    }
    let alertResolveAction: AlertAction | undefined
    if (showAlert.onResolve) {
        alertResolveAction = {title: showAlert.onResolve.title, onClick: () => {
            onCloseAlert()
            showAlert.onResolve?.onClick && differ(showAlert.onResolve.onClick)
        }}
    }

    if (!alertResolveAction && !alertRejectAction) {
        alertResolveAction = {title: "Okay", onClick: () => {
            onCloseAlert()
        }}
    }

    const $alert = (
        <Alert
            isOpen={showAlert.isVisible}
            title={showAlert.title}
            description={showAlert.message}
            rejectAction={alertRejectAction}
            resolveAction={alertResolveAction}
        />
    )

    const $spinner = (<Spinner isOpen={isShowSpinner} />)
    const $content = provider ? (
        <ProviderContext.Provider value={provider}>
            <GraylogContext.Provider value={getLogEmployee(provider)}>
                {$routerContent}
            </GraylogContext.Provider>
        </ProviderContext.Provider>
    ) : null
    return (
        <SharedContextProvider>
            <PromptContext.Provider value={promptContextValue}>
                <div className={"w-full min-h-screen bg-gray-6"}>
                    <NavBar data={NaviBarData}/>
                    {$content}
                    {$spinner}
                    {$toastNotification}
                    {$alert}
                </div>
            </PromptContext.Provider>
        </SharedContextProvider>
    )
}

export default Admin
